import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Clock, ExternalLink, Loader2, MessageSquare, RefreshCw } from 'lucide-react';
import supabase from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';

const STATUS_TABS = ['open', 'reviewed', 'resolved'];

const statusConfig = {
  open:     { label: 'Open',     color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  reviewed: { label: 'Reviewed', color: 'bg-blue-100 text-blue-700 border-blue-200',    dot: 'bg-blue-500' },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-700 border-green-200',  dot: 'bg-green-500' },
};

const getRelativeTime = (ts) => {
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 7)  return `${d}d ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export default function EscalationsDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [escalations, setEscalations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('open');
  const [resolvingId, setResolvingId] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [showResolveFor, setShowResolveFor] = useState(null);

  const fetchEscalations = useCallback(async () => {
    setLoading(true);

    const { data, error } = await supabase
      .from('escalations')
      .select('*, leads(name, phone, status), tenants(name)')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setEscalations(data);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchEscalations();
  }, [fetchEscalations]);

  const handleStatusChange = async (id, newStatus) => {
    setResolvingId(id);
    const updates = { status: newStatus };

    if (newStatus === 'resolved') {
      updates.resolved_at = new Date().toISOString();
      updates.resolved_by = user.id;
      if (resolutionNotes.trim()) {
        updates.resolution_notes = resolutionNotes.trim();
      }
    }

    const { error } = await supabase
      .from('escalations')
      .update(updates)
      .eq('id', id);

    if (!error) {
      setEscalations(prev =>
        prev.map(e => e.id === id ? { ...e, ...updates } : e)
      );
    }
    setResolvingId(null);
    setShowResolveFor(null);
    setResolutionNotes('');
  };

  const filtered = escalations.filter(e => e.status === activeTab);

  const counts = {
    open: escalations.filter(e => e.status === 'open').length,
    reviewed: escalations.filter(e => e.status === 'reviewed').length,
    resolved: escalations.filter(e => e.status === 'resolved').length,
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Escalations</h1>
            <p className="text-sm text-gray-500">Issues reported by tenants</p>
          </div>
        </div>
        <button
          onClick={fetchEscalations}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        {STATUS_TABS.map(tab => {
          const cfg = statusConfig[tab];
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`relative px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === tab ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {cfg.label}
              {counts[tab] > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium border ${
                  activeTab === tab ? cfg.color : 'bg-gray-100 text-gray-500 border-gray-200'
                }`}>
                  {counts[tab]}
                </span>
              )}
              {activeTab === tab && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900 rounded-t" />
              )}
            </button>
          );
        })}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            {activeTab === 'resolved' ? (
              <CheckCircle2 className="w-5 h-5 text-gray-400" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-gray-400" />
            )}
          </div>
          <p className="text-sm font-medium text-gray-700">
            No {activeTab} escalations
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {activeTab === 'open' ? 'All clear, nothing in the queue right now.' : `No ${activeTab} escalations to show.`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(esc => {
            const leadName = esc.leads?.name || 'Unknown Lead';
            const tenantName = esc.tenants?.name || 'Unknown tenant';
            const cfg = statusConfig[esc.status];
            return (
              <div
                key={esc.id}
                className="bg-white border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Tenant + lead name + time */}
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-full">
                        {tenantName}
                      </span>
                      <button
                        onClick={() => navigate(`/lead/${esc.lead_id}`)}
                        className="text-sm font-semibold text-gray-900 hover:text-indigo-600 transition-colors flex items-center gap-1"
                      >
                        {leadName}
                        <ExternalLink className="w-3 h-3" />
                      </button>
                      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {getRelativeTime(esc.created_at)}
                      </span>
                    </div>

                    {/* Reason */}
                    <div className="flex items-start gap-2 mt-2">
                      <MessageSquare className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-gray-600">{esc.reason}</p>
                    </div>

                    {/* Resolution notes */}
                    {esc.resolution_notes && esc.status === 'resolved' && (
                      <div className="mt-2 ml-5 px-3 py-2 bg-green-50 border border-green-100 rounded-lg">
                        <p className="text-xs text-green-700">{esc.resolution_notes}</p>
                      </div>
                    )}

                    {/* Inline resolve form */}
                    {showResolveFor === esc.id && (
                      <div className="mt-3 ml-5 space-y-2">
                        <textarea
                          value={resolutionNotes}
                          onChange={(e) => setResolutionNotes(e.target.value)}
                          placeholder="Resolution notes (optional)"
                          rows={2}
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-400 resize-none"
                          autoFocus
                        />
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleStatusChange(esc.id, 'resolved')}
                            disabled={resolvingId === esc.id}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-green-500 hover:bg-green-600 disabled:opacity-50 rounded-lg transition-colors"
                          >
                            {resolvingId === esc.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                            Resolve
                          </button>
                          <button
                            onClick={() => { setShowResolveFor(null); setResolutionNotes(''); }}
                            className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  {esc.status !== 'resolved' && showResolveFor !== esc.id && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {esc.status === 'open' && (
                        <button
                          onClick={() => handleStatusChange(esc.id, 'reviewed')}
                          disabled={resolvingId === esc.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                        >
                          {resolvingId === esc.id ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                          Mark Reviewed
                        </button>
                      )}
                      <button
                        onClick={() => setShowResolveFor(esc.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                      >
                        Resolve
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
